"use client"

import React, { type ComponentType, useState } from "react"
import type { PreviewData, ViewerProps } from "@/lib/types"
import { handleTakePicture } from "@/utils/mediaHandlers"
import FloatingActionButton from "@/components/floating-action-button"
import FloatingAutoRotateButton from "@/components/floating-auto-rotate-button"
import GesturesHelp from "./gestures-help"
import { PWAInstallPrompt } from "@/components/pwa-install-prompt"

const Viewer = React.lazy<ComponentType<ViewerProps>>(() =>
  import("@/components/Viewer").then((module) => ({ default: module.Viewer })),
)

interface ViewerContentProps {
  preview: PreviewData
  SubPreview?: PreviewData | null
}

export default function ViewerContent({ preview, SubPreview }: ViewerContentProps) {
  const [autoRotate, setAutoRotate] = useState(true)

  const toggleAutoRotate = () => {
    setAutoRotate(!autoRotate)
  }

  const handleTakePictureWrapper = async () => {
    await handleTakePicture()
  }

  return (
    <>
      <div className="flex-1 relative w-full">
        <div className="absolute inset-0">
          <div className="w-full h-full viewer-container">
            <Viewer
              PreviewObj={preview}
              SubPreviewObj={SubPreview}
              preserveDrawingBuffer={true}
              autoRotate={autoRotate}
            />
          </div>
        </div>        {/* Title and Help Button - Top Center */}
        {preview && (
          <div className="absolute top-4 left-0 right-0 flex justify-center items-center z-10">
            <div className="bg-background/60 backdrop-blur-md rounded-lg py-2 px-4 flex items-center gap-2 shadow-md transition-all hover:bg-background/70">
              <div className="flex flex-col sm:flex-row sm:items-center items-center">
                <strong className="text-sm sm:text-base">{preview.name}</strong>
                {preview.model?.name && (
                  <span className="text-xs sm:text-sm sm:ml-1 text-muted-foreground">{preview.model.name}</span>
                )}
              </div>
              <PWAInstallPrompt isCollapsed={true} />

              <GesturesHelp className="ml-1" />
            </div>
          </div>
        )}

        {/* Copyright text with bottom middle position and low opacity */}
        <div className="absolute bottom-2 left-0 right-0 flex justify-center z-10 pointer-events-none">
          <div className="text-[10px] sm:text-xs text-white dark:text-muted-foreground bg-background/30 backdrop-blur-sm px-2 py-1 rounded-full opacity-100">
            Shared using Atlasimex 3D Previewer &copy; {new Date().getFullYear()}

          </div>
        </div>
      </div>

      {/* Floating Action Button */}
      <FloatingActionButton
        onTakePicture={handleTakePictureWrapper}
      />

      {/* Floating Auto-Rotate Button */}
      <FloatingAutoRotateButton
        autoRotate={autoRotate}
        toggleAutoRotate={toggleAutoRotate}
      /></>
  )
}

